import React, { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendEmailVerification,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { auth, db } from '../utils/firebase-config';
import '../styles/login.css';
import TwoFactorSetup from './TwoFactorSetup';
import TwoFactorVerify from './TwoFactorVerify';
import '../styles/twofa.css';

export default function LoginForm() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [territory, setTerritory] = useState('IE');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [registrationSource, setRegistrationSource] = useState(null);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [showTwoFactorVerify, setShowTwoFactorVerify] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentGido, setCurrentGido] = useState(null);
  const [resetOobCode, setResetOobCode] = useState(null);
  const [resetEmail, setResetEmail] = useState('');

  const territories = [
    { code: 'IE', name: 'Ireland' },
    { code: 'DE', name: 'Germany' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'AT', name: 'Austria' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'EU', name: 'Other (EU)' },
    { code: 'XX', name: 'Other (Worldwide)' },
  ];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMode = params.get('mode');
    const oobCode = params.get('oobCode');
    
    if (urlMode === 'verifyEmail' && oobCode) {
      applyActionCode(auth, oobCode)
        .then(() => {
          setMode('login');
          alert('✅ Email verified successfully!\n\nYou can now log in with your credentials.');
          window.history.replaceState({}, '', '/');
        })
        .catch((err) => {
          console.error('Verification error:', err);
          alert('⚠️ Verification link expired or already used.\n\nPlease try logging in anyway, or contact admin@dbos.world');
          window.history.replaceState({}, '', '/');
        });
    }

    if (urlMode === 'verifyAndChangeEmail' && oobCode) {
      checkActionCode(auth, oobCode)
        .then((info) => {
          const newEmail = info?.data?.email || '';
          return applyActionCode(auth, oobCode).then(() => newEmail);
        })
        .then((newEmail) => {
          setMode('login');
          alert('✅ Your email address has been changed successfully!\n\nYour DBOS Global Identity (GIDO) stays the same — only your login email has changed.\n\nPlease log in with your NEW email address' + (newEmail ? ': ' + newEmail : '.') + '\n\nYour 2FA was reset — you will set it up again on next login. Please delete the old "DBOS" entry from your authenticator app.');
          window.history.replaceState({}, '', '/');
        })
        .catch((err) => {
          console.error('Email change error:', err);
          alert('⚠️ Email change link expired or already used.\n\nPlease try again from your portal settings, or contact admin@dbos.world');
          window.history.replaceState({}, '', '/');
        });
    }

    if (urlMode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setResetEmail(email);
          setResetOobCode(oobCode);
          setMode('reset');
          window.history.replaceState({}, '', '/');
        })
        .catch((err) => {
          console.error('Password reset error:', err);
          alert('⚠️ Password reset link expired or already used.\n\nPlease request a new one from portal.dbos.world, or contact admin@dbos.world');
          window.history.replaceState({}, '', '/');
        });
    }
    
    const product = params.get('product');
    if (product) {
      setRegistrationSource(product);
      setMode('register');
    }
  }, []);

  // Generate next GIDO number for territory
  // Uses Firestore transaction to prevent race conditions
  const getNextGidoNumber = async (territoryCode) => {
    const counterRef = doc(db, 'gido_counters', territoryCode);
    const digits     = territoryCode === 'DE' ? 5 : 4;

    try {
      const nextNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);

        if (!counterDoc.exists()) {
          transaction.set(counterRef, { current: 1, territory: territoryCode });
          return 1;
        }

        const current = counterDoc.data().current || 0;
        const next    = current + 1;
        transaction.update(counterRef, { current: next });
        return next;
      });

      return String(nextNumber).padStart(digits, '0');

    } catch (error) {
      console.error('Error getting next GIDO number:', error);
      throw error;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!displayName.trim()) {
        throw new Error('Full name is required');
      }
      
      if (!companyName.trim()) {
        throw new Error('Company name is required');
      }
      
      // Password policy
      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      if (!/[A-Z]/.test(password)) {
        throw new Error('Password must contain at least one uppercase letter');
      }
      if (!/[0-9!@#$%^&*()_+\-=\[\]{};:,.<>?]/.test(password)) {
        throw new Error('Password must contain at least one number or special character');
      }
      if (/^(.)\1+$/.test(password)) {
        throw new Error('Password cannot be all the same character');
      }
      const COMMON_PASSWORDS = [
        'password', 'password1', 'password123', '12345678', '123456789',
        'qwerty123', 'qwertyuiop', 'letmein123', 'welcome123', 'admin1234',
        'iloveyou1', 'test1234!', 'changeme1',
      ];
      if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
        throw new Error('This password is too common — please choose a stronger one');
      }
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Sanitize inputs
      const sanitize = (str) => str
        .replace(/[<>'"]/g, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+=/gi, '')
        .trim();

      const cleanDisplayName  = sanitize(displayName);
      const cleanCompanyName  = sanitize(companyName);

      if (cleanDisplayName.length < 2) {
        throw new Error('Please enter a valid full name');
      }
      if (cleanCompanyName.length < 2) {
        throw new Error('Please enter a valid company name');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;
      
      // Generate proper GIDO with territory and sequential numbering
      const number = await getNextGidoNumber(territory);
      const gidoId = `GIDO_${territory}_${number}`;
      
      await setDoc(doc(db, 'gido_registry', uid), {
        gido_id: gidoId,
        entity_type: 'CLIENT',
        display_name: cleanCompanyName,
        full_name: cleanDisplayName,
        company_name: cleanCompanyName,
        email: email,
        territory_id: territory,
        jurisdiction: territory, // Keep for backwards compatibility

        // For EU territory, flag for future migration
        needs_territory_assignment: territory === 'EU' ? true : false,
        
        // Access control
        trust_level: 'T1',
        role: 'client',
        layer_access: 2, // Keep for backwards compatibility
        
        // Layer access (T1 default)
        layers: {
          layer_0: true,   // GIDO, auth
          layer_I: false,  // Intake (unlocks at 50GB SmartData)
          layer_II: false, // Dashboard (unlocks with first product)
          layer_III: false, // Regional context (Boss Suite)
          layer_IV: false,  // Automation (Boss Suite)
          layer_V: false,   // Financial (read-only for T1)
          layer_VI: false,  // Tax structuring (T5 only)
        },
        
        status: 'PENDING_VERIFICATION',
        parent_gido: 'GIDO_C_0000',
        twofa_enabled: false,
        created_at: new Date(),
      });

      // Send email verification
      await sendEmailVerification(userCredential.user);

      // Redirect based on registration source
      if (registrationSource === 'smartdata') {
        window.location.href = `https://dbos.world/welcome?gido=${gidoId}&setup=smartdata`;
      } else if (registrationSource === 'smartmail') {
        window.location.href = `https://dbos.world/welcome?gido=${gidoId}&setup=smartmail`;
      } else {
        alert(`✅ Account created!\n\nGIDO: ${gidoId}\n\n📧 Please check your email (${email}) and click the verification link before logging in.\n\n⚠️ Check your Spam folder if you don't see it in your inbox.`);
        setMode('login');
        setEmail('');
        setPassword('');
        setDisplayName('');
        setCompanyName('');
        setTerritory('IE');
        setLoading(false);
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Reload user to get latest emailVerified status
      await userCredential.user.reload();

      // ✅ Use auth.currentUser — this reflects the reloaded state
      await userCredential.user.reload();
      const freshUser = userCredential.user;
      const uid = freshUser.uid;

      if (!freshUser.emailVerified) {
        throw new Error('⚠️ Please verify your email before logging in.\n\nCheck your inbox for the verification link.\n\nDidn\'t receive it? Contact admin@dbos.world');
      }
      
      // Two kinds of person can log in here:
      //   a GIDO owner  - has a gido_registry record (their own organisation)
      //   a GIDU seat   - has a gidu_registry record only (they work inside someone
      //                   else's organisation; D-024 keeps these distinct)
      // An owner's record is checked first; a seat is the fallback.
      const gidoRef = doc(db, 'gido_registry', uid);
      const gidoSnap = await getDoc(gidoRef);

      let gido = null;
      let isSeat = false;

      if (gidoSnap.exists()) {
        gido = gidoSnap.data();

        // Sync email if changed via verifyAndChangeEmail (Auth is source of truth).
        // GIDO is NEVER touched - only the login-email credential field is synced.
        if (freshUser.email && gido.email !== freshUser.email) {
          await setDoc(gidoRef, { email: freshUser.email }, { merge: true });
          gido.email = freshUser.email;
        }
      } else {
        // dbos-gidu 0.3.0: seats are keyed {uid}:{gido}, so a document get on the
        // uid alone finds nothing. Query the uid field instead. A login can hold
        // several seats; the org switcher picks between them after sign-in, so the
        // first is only used to get through 2FA and the redirect.
        const seatQuery = await getDocs(
          query(collection(db, 'gidu_registry'), where('uid', '==', uid))
        );
        const seatSnap = seatQuery.docs[0];
        if (!seatSnap) {
          throw new Error('No DBOS access found for this login. Contact admin@dbos.world');
        }
        const seat = seatSnap.data();
        isSeat = true;
        // A seat carries no trust_level or territory - it is not an organisation.
        // Build the minimum the 2FA components and the redirect need.
        gido = {
          gido_id: seat.owner_gido,
          display_name: seat.name || freshUser.email,
          email: freshUser.email,
          is_seat: true,
        };
      }

      setCurrentUser(userCredential.user);
      setCurrentGido(gido);

      // Reset the 2FA claim so THIS session must re-verify before any data access
      let enrolled = false;
      try {
        const t = await auth.currentUser.getIdToken();
        await fetch('https://europe-west1-dbos-core-prod.cloudfunctions.net/twofa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
          body: JSON.stringify({ action: 'reset' }),
        });

        // Ask the backend whether this person is enrolled, rather than reading a
        // mirrored flag off gido_registry. twofa_secrets is the real state and is
        // rules-locked, so only the function can read it - and a GIDU has no
        // gido_registry flag to read in the first place.
        const statusRes = await fetch('https://europe-west1-dbos-core-prod.cloudfunctions.net/twofa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
          body: JSON.stringify({ action: 'status' }),
        });
        const statusData = await statusRes.json();
        enrolled = !!statusData.enrolled;
      } catch (resetErr) {
        console.warn('2FA state check failed:', resetErr);
        // Fail closed: if we cannot confirm enrolment, send them through setup rather
        // than skipping the second factor.
        enrolled = false;
      }

      if (enrolled) {
        setShowTwoFactorVerify(true);
      } else {
        setShowTwoFactorSetup(true);
      }
      setLoading(false);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const redirectByRole = (gido) => {
    const { trust_level, territory_id, gido_id, is_seat } = gido;

    // A seat holder works inside someone else's organisation - they have no
    // trust_level and no territory of their own. Always the portal.
    if (is_seat) {
      window.location.href = `https://portal.dbos.world?gido=${gido_id}`;
      return;
    }

    if (trust_level === 'T5' || trust_level === 'T4') {
      window.location.href = 'https://dbos.world/master-control';
    } else if (trust_level === 'T3' && territory_id) {
      const territoryCode = territory_id.toLowerCase();
      window.location.href = `https://territory-${territoryCode}.dbos.world/admin`;
    } else {
      window.location.href = `https://portal.dbos.world?gido=${gido_id}&welcome=true`;
    }
  };

  const handle2FASetupComplete = async () => {
    setShowTwoFactorSetup(false);
    try { await auth.currentUser.getIdToken(true); } catch (e) {}
    redirectByRole(currentGido);
  };

  const handle2FAVerifySuccess = async () => {
    setShowTwoFactorVerify(false);
    try { await auth.currentUser.getIdToken(true); } catch (e) {}
    redirectByRole(currentGido);
  };
  
  const handle2FACancel = () => {
    // User cancelled 2FA - log them out
    setShowTwoFactorVerify(false);
    setEmail('');
    setPassword('');
    setCurrentUser(null);
    setCurrentGido(null);
    auth.signOut();
  };

  // Show 2FA Setup screen
  if (showTwoFactorSetup) {
    return (
      <TwoFactorSetup 
        user={currentUser}
        gido={currentGido}
        onComplete={handle2FASetupComplete}
      />
    );
  }

  // Show 2FA Verify screen
  if (showTwoFactorVerify) {
    return (
      <TwoFactorVerify 
        gido={currentGido}
        onSuccess={handle2FAVerifySuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await confirmPasswordReset(auth, resetOobCode, password);
      setResetOobCode(null);
      setPassword('');
      setConfirmPassword('');
      setMode('login');
      alert('✅ Password changed successfully!\n\nPlease sign in with your new password.\n\nYou will then be asked to set up two-factor authentication.');
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Reset link expired or already used. Please request a new one from portal.dbos.world');
    }
    setLoading(false);
  };

  if (mode === 'reset' && resetOobCode) {
    return (
      <div className="login-container">
        <div className="dbos-logo-section">
          <div className="dbos-logo-container">
            <img src="/assets/DBOS_Logo_without_background.png" alt="DBOS Logo" className="dbos-logo" />
          </div>
          <div className="dbos-divider"></div>
          <h1 className="dbos-tagline dbos-shimmer">The Architecture of Digital Trust</h1>
        </div>

        <form onSubmit={handleResetSubmit} className="login-form">
          <h2>Set Your New Password</h2>
          <p className="login-subtitle">for {resetEmail}</p>

          {error && <div className="error-message">{error}</div>}

          <div className="input-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              type="password"
              placeholder="Minimum 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={8}
              autoComplete="new-password"
              autoFocus
            />
          </div>

          <div className="input-group">
            <label htmlFor="confirmNewPassword">Confirm New Password</label>
            <input
              id="confirmNewPassword"
              type="password"
              placeholder="Repeat your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Saving...' : 'Set New Password'}
          </button>

          <p className="login-subtitle" style={{ marginTop: '16px' }}>
            After saving, sign in with your new password. You'll then set up two-factor authentication — please delete any old "DBOS" entry from your authenticator app first.
          </p>
        </form>
      </div>
    );
  }

  // Show normal login form
  return (
    <div className="login-container">
      {/* Logo Section */}
      <div className="dbos-logo-section">
        <div className="dbos-logo-container">
          <img 
            src="/assets/DBOS_Logo_without_background.png" 
            alt="DBOS Logo" 
            className="dbos-logo"
          />
        </div>
        
        <div className="dbos-divider"></div>
        
        <h1 className="dbos-tagline dbos-shimmer">
          The Architecture of Digital Trust
        </h1>
      </div>

      {/* Login/Register Form */}
      <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="login-form">
        <h2>
          {mode === 'login' 
            ? 'Welcome Back' 
            : registrationSource 
              ? `Get Started with Smart${registrationSource.charAt(0).toUpperCase() + registrationSource.slice(1)}` 
              : 'Create Account'
          }
        </h2>
        
        <p className="login-subtitle">
          {mode === 'login' ? 'Sign in to access your portal' : 'Join the DBOS ecosystem'}
        </p>

        {error && <div className="error-message">{error}</div>}

        {/* REGISTRATION FIELDS */}
        {mode === 'register' && (
          <>
            {/* Full Name */}
            <div className="input-group">
              <label htmlFor="displayName">Your Name</label>
              <input
                id="displayName"
                type="text"
                placeholder="Full name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {/* Company Name - NEW */}
            <div className="input-group">
              <label htmlFor="companyName">Company Name *</label>
              <input
                id="companyName"
                type="text"
                placeholder="Your business or personal brand"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
                required
              />
              <small className="field-hint">
                Business or personal brand name (e.g., "John Doe Consulting")
              </small>
            </div>

            {/* Territory - NEW */}
            <div className="input-group">
              <label htmlFor="territory">Country / Territory *</label>
              <select
                id="territory"
                value={territory}
                onChange={(e) => setTerritory(e.target.value)}
                disabled={loading}
                required
                className="territory-select"
              >
                {territories.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
              <small className="field-hint">
                Your data will be stored in this region for GDPR compliance
              </small>
            </div>
          </>
        )}

        {/* EMAIL (both login and register) */}
        <div className="input-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete="email"
          />
        </div>

        {/* PASSWORD (both login and register) */}
        <div className="input-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            placeholder="Minimum 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            minLength={8}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </div>
        {mode === 'register' && (
          <div className="input-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              minLength={8}
              autoComplete="new-password"
            />
          </div>
        )}

        <button type="submit" disabled={loading} className="login-button">
          {loading ? (
            <>
              <span className="loading-spinner"></span>
              <span style={{marginLeft: '8px'}}>Please wait...</span>
            </>
          ) : mode === 'login' ? 'Sign In' : 'Create Account'}
        </button>

        <div className="mode-switch">
          {mode === 'login' ? (
            <p>
              Don't have an account?{' '}
              <button type="button" onClick={() => setMode('register')} className="link-button">
                Register here
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button type="button" onClick={() => setMode('login')} className="link-button">
                Sign in here
              </button>
            </p>
          )}
        </div>
      </form>

      {/* Footer */}
      <footer className="login-footer">
        <p>© 2026 DBOS World · <a href="mailto:admin@dbos.world">Support</a></p>
      </footer>
    </div>
  );
}
